import React from 'react';
import { styled } from '@compiled/react';

import type { XCSS } from '@atlaskit/primitives';
import { Box, xcss } from '@atlaskit/primitives';

import { EmojiTitleSelection } from '@confluence/emoji-title';
import { useIsLivePage } from '@confluence/live-pages-utils/entry-points/useIsLivePage';
import { useCreateSpaceExperiment } from '@confluence/create-space/entry-points/createSpace';
import { COVER_IMAGE_FIXED_WIDTH } from '@confluence/page-cover-picture';
import { useIsFixedWidthImageOption } from '@confluence/custom-sites-extensions';
import { useBooleanFeatureFlag } from '@confluence/session-data';

import type { TitleSectionProps } from './titleareaContentComponentsTypes';

const mainBoxStylesBase = xcss({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
});

const mainBoxStylesGapSpace0 = xcss({
	gap: 'space.0',
});

const mainBoxStylesGapSpace300 = xcss({
	gap: 'space.300',
});

const titleBoxStylesBase = xcss({
	position: 'relative',
	width: '100%',
});

const titleBoxStylesDisplayFlex = xcss({
	display: 'flex',
});

const titleBoxStylesDisplayInlineFlex = xcss({
	display: 'inline-flex',
});

const titleBoxStylesFlexDirectionColumn = xcss({
	flexDirection: 'column',
});

const titleBoxStylesFlexDirectionInitial = xcss({
	flexDirection: 'initial',
});

const titleBoxStylesGapSpace050 = xcss({
	gap: 'space.050',
});

const titleBoxStylesMarginTopMinus90px = xcss({
	// @ts-ignore TODO: refactor to remove need for hardcoded `-90px`
	marginTop: '-90px',
});

const titleBoxStylesMarginTopInitial = xcss({
	marginTop: 'initial',
});

const titleBoxStylesMarginTopSpace200 = xcss({
	marginTop: 'space.200',
});

const titleBoxStylesMarginBottomSpace400 = xcss({
	marginBottom: 'space.400',
});

const titleBoxStylesMarginBottomSpace050 = xcss({
	marginBottom: 'space.050',
});

const titleBoxStylesMarginBottomInitial = xcss({
	marginBottom: 'initial',
});

const titleBoxStylesAlignItemsCenter = xcss({
	alignItems: 'center',
});

const titleBoxStylesAlignItemsInitial = xcss({
	alignItems: 'initial',
});

const contentTopperBoxStylesBase = xcss({
	alignSelf: 'normal',
});

const contentTopperBoxStylesMarginTopSpace0 = xcss({
	marginTop: 'space.0',
});

const contentTopperBoxStylesMarginTopSpaceNegative300 = xcss({
	marginTop: 'space.negative.300',
});

const emojiHiddenStyles = xcss({
	visibility: 'hidden',
});

const emojiHeightStyles = xcss({
	height: '88px',
});

const emojiMarginTopSpace300Styles = xcss({
	marginTop: 'space.300',
});

const emojiMarginTopSpace0Styles = xcss({
	marginTop: 'space.0',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiTitleSelectionWrapper = styled.div<{
	isTitleCenterAligned?: boolean;
	hasCoverPicture?: boolean;
	isCustomSitesPageTitleFFOn?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: ({ isCustomSitesPageTitleFFOn, isTitleCenterAligned, hasCoverPicture }) =>
		isCustomSitesPageTitleFFOn && (isTitleCenterAligned || hasCoverPicture) ? 'flex' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: ({ isTitleCenterAligned, isCustomSitesPageTitleFFOn }) =>
		isCustomSitesPageTitleFFOn && isTitleCenterAligned ? 'center' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	alignItems: ({ isTitleCenterAligned, isCustomSitesPageTitleFFOn }) =>
		isCustomSitesPageTitleFFOn && isTitleCenterAligned ? 'center' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	flexDirection: ({ isCustomSitesPageTitleFFOn, isTitleCenterAligned, hasCoverPicture }) =>
		isCustomSitesPageTitleFFOn && (isTitleCenterAligned || hasCoverPicture) ? 'column' : 'row',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ hasCoverPicture, isCustomSitesPageTitleFFOn }) =>
		isCustomSitesPageTitleFFOn && hasCoverPicture ? 'fit-content' : 'initial',
});

const livePageTitleStyles = xcss({
	marginTop: 'space.300',
});
const baseEditorTitleStyles = xcss({
	width: '100%',
});
const fixedWidthLeftAlignImageStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${COVER_IMAGE_FIXED_WIDTH}px`,
	margin: '0 auto',
	paddingLeft: 'space.250',
});

export const TitleSection = ({
	contentId,
	isTitleDisabled,
	linkCreation,
	editorTitle,
	isTemplateEditor,
	onUpdateStartEmoji,
	onUpdateEndEmoji,
	isTitleCenterAligned,
	hasCoverPicture,
	hasEmoji,
	isCustomSitesPageTitleFFOn,
	isRemovingEmoji,
	isTemplateSuggestionsEnabled,
	contentTopperComponent,
	isReadOnly,
	coverPictureWidth,
	isPageContentFullWidth,
	isTitleAlignmentLoading,
}: TitleSectionProps) => {
	const isLivePage = useIsLivePage();

	const isNewLivePageBylineFeatureFlagEnabled = useBooleanFeatureFlag(
		'confluence.frontend.custom-sites.new-live-pages-byline',
	);
	const isNewLivePageByline =
		isCustomSitesPageTitleFFOn && isNewLivePageBylineFeatureFlagEnabled && isLivePage;
	const isCustomSitesWithNewLayout =
		isCustomSitesPageTitleFFOn && (isTitleCenterAligned || hasCoverPicture);
	const { isSpaceTypesCreationFlowEnabled } = useCreateSpaceExperiment();

	const isFixedWidth = useIsFixedWidthImageOption({ coverPictureWidth });
	const isFixedWidthImageOption = isCustomSitesPageTitleFFOn && isFixedWidth;

	const marginTopStyleForTitleWithCoverPicture =
		isCustomSitesPageTitleFFOn && hasCoverPicture
			? titleBoxStylesMarginTopMinus90px
			: titleBoxStylesMarginTopInitial;

	const titleXcss: Array<XCSS | undefined> = [
		titleBoxStylesBase,
		// display
		isCustomSitesPageTitleFFOn ? titleBoxStylesDisplayFlex : titleBoxStylesDisplayInlineFlex,
		// flexDirection
		isCustomSitesPageTitleFFOn && (isTitleCenterAligned || hasCoverPicture)
			? titleBoxStylesFlexDirectionColumn
			: titleBoxStylesFlexDirectionInitial,
		// gap
		(hasCoverPicture || (isTitleCenterAligned && hasEmoji)) && isCustomSitesPageTitleFFOn
			? titleBoxStylesGapSpace050
			: undefined,
		// marginBottom
		isCustomSitesPageTitleFFOn && !isTemplateSuggestionsEnabled
			? isNewLivePageByline && !isCustomSitesWithNewLayout
				? titleBoxStylesMarginBottomSpace050
				: titleBoxStylesMarginBottomSpace400
			: titleBoxStylesMarginBottomInitial,
		// alignItems
		isCustomSitesPageTitleFFOn && hasCoverPicture && isTitleCenterAligned
			? titleBoxStylesAlignItemsCenter
			: titleBoxStylesAlignItemsInitial,
		// marginTop
		marginTopStyleForTitleWithCoverPicture,
	];

	const emojiComponent =
		!isTemplateEditor && (!isRemovingEmoji || hasCoverPicture) ? (
			<Box
				xcss={[
					hasCoverPicture && isCustomSitesPageTitleFFOn && emojiHeightStyles,
					isRemovingEmoji && hasCoverPicture && emojiHiddenStyles,
					isLivePage && !(hasCoverPicture && isCustomSitesPageTitleFFOn)
						? emojiMarginTopSpace300Styles
						: emojiMarginTopSpace0Styles,
					hasCoverPicture &&
						isFixedWidthImageOption &&
						isPageContentFullWidth &&
						!isTitleCenterAligned &&
						fixedWidthLeftAlignImageStyles,
				]}
			>
				{isCustomSitesPageTitleFFOn && isTitleAlignmentLoading ? null : (
					<EmojiTitleSelectionWrapper
						data-testid={
							isTitleCenterAligned
								? 'emoji-title-selection-wrapper-centered'
								: 'emoji-title-selection-wrapper'
						}
						isTitleCenterAligned={isTitleCenterAligned}
						isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
						hasCoverPicture={hasCoverPicture}
					>
						<EmojiTitleSelection
							contentId={contentId}
							onUpdateStartEmoji={onUpdateStartEmoji}
							onUpdateEndEmoji={onUpdateEndEmoji}
							isTitleDisabled={isTitleDisabled}
							isTitleCenterAligned={isTitleCenterAligned}
							hasCoverPicture={!!hasCoverPicture}
							linkCreation={linkCreation}
							isReadOnly={isReadOnly}
							isSpaceTypesCreationFlowEnabled={isSpaceTypesCreationFlowEnabled}
						/>
					</EmojiTitleSelectionWrapper>
				)}
			</Box>
		) : null;

	const titleSectionStyles = [
		mainBoxStylesBase,
		isCustomSitesPageTitleFFOn && !hasCoverPicture
			? mainBoxStylesGapSpace0
			: mainBoxStylesGapSpace300,
		isNewLivePageByline && !isCustomSitesWithNewLayout && titleBoxStylesMarginBottomSpace400,
	];

	const renderContentTopper = () => {
		const contentTopperStyles = [
			(!isNewLivePageByline || isCustomSitesWithNewLayout) && contentTopperBoxStylesBase,
			(!isNewLivePageByline || isCustomSitesWithNewLayout) && isLivePage
				? contentTopperBoxStylesMarginTopSpace0
				: (hasCoverPicture || (hasEmoji && !isRemovingEmoji)) &&
					contentTopperBoxStylesMarginTopSpaceNegative300,
			isNewLivePageByline && titleBoxStylesMarginTopSpace200,
		];
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
		return <Box xcss={contentTopperStyles}>{contentTopperComponent}</Box>;
	};

	const renderEditorTitle = () => {
		const editorTitleStyles = [
			baseEditorTitleStyles,
			isLivePage &&
				(!isNewLivePageByline || !isTitleCenterAligned || hasEmoji || hasCoverPicture) &&
				livePageTitleStyles,
		];
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
		return <Box xcss={editorTitleStyles}>{editorTitle}</Box>;
	};

	return (
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
		<Box xcss={titleSectionStyles}>
			{!isCustomSitesWithNewLayout && !isNewLivePageByline && contentTopperComponent}
			<Box
				// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
				xcss={titleXcss}
				testId={isTitleCenterAligned ? 'title-wrapper-centered' : 'title-wrapper'}
			>
				{emojiComponent}
				{isCustomSitesWithNewLayout &&
					!isNewLivePageByline &&
					contentTopperComponent &&
					renderContentTopper()}

				{renderEditorTitle()}

				{isCustomSitesWithNewLayout &&
					isNewLivePageByline &&
					contentTopperComponent &&
					renderContentTopper()}
			</Box>
			{!isCustomSitesWithNewLayout &&
				isNewLivePageByline &&
				contentTopperComponent &&
				renderContentTopper()}
		</Box>
	);
};
